import RequestToken from '../../../../tokens/RequestToken';
import {CommandRequester} from '../../../../CommandRequester';
import RequestAPI from '../../../RequestAPI';
import {Event} from '../../../../events/Event';
import {AsyncCommandEvent, ViewStates, DisplayEvents, CommandTypes} from '@jibo/command-protocol';


/**
 * @class DisplayToken
 * @extends RequestToken
 * @hideconstructor
 */
export class DisplayToken extends RequestToken<JIBO.v1.DisplayRequest> {
    /**
     * Emitted when a display view is opened.
     * @name DisplayToken#opened
     * @type {Event}
     */
    public opened:Event<void>;


    /** @private */
    constructor(owner: CommandRequester, protocol: JIBO.v1.DisplayRequest) {
        super(owner, protocol);
        this.opened = new Event('Opened');
    }

    /**
     * @private
     */
    public handleAck(ack:JIBO.v1.Acknowledgement) {
        //handle ack
        if (ack.Response.ResponseCode >= 400) {
            this.isComplete = true;
            this._complete.reject(ack.Response);
            return;
        }
    }

    /**
     * @private
     */
    public handleEvent(event:JIBO.v1.EventMessage) {
        //handle event
        const eventData = event.EventBody;
        switch (eventData.Event) {
            case AsyncCommandEvent.Stop:
                this.isComplete = true;
                this._complete.resolve();
                break;
            case AsyncCommandEvent.Error:
                this.isComplete = true;
                this._complete.reject(eventData);
                break;
            case DisplayEvents.ViewStateChange:
                switch (eventData.State) {
                    case ViewStates.Opened:
                        this.opened.emit();
                        break;
                }
        }
    }
}

/**
 * Internal Display class
 * @class CommandRequester.display.Display
 * @intdocs
 */
export class Display extends RequestAPI<JIBO.v1.DisplayRequest, DisplayToken> {
    
    /**
     * Generate Display Protocol
     * @method CommandRequester.display.Display#generateProtocol
     * @param {(CommandRequester.display#EyeView | CommandRequester.display#TextView | CommandRequester.display#ImageView)} view - View to replace the existing one with.
     * @param  {DisplayTransitionType} [openTransition] Transition type to open.
     * @param  {DisplayTransitionType} [closeTransition] Transition type to close.
     * @returns {DisplayRequest}
     * @intdocs
     */
    static generateProtocol(
      view: JIBO.v1.EyeView | JIBO.v1.TextView | JIBO.v1.ImageView,
      openTransition?:JIBO.v1.DisplayTransitions.DisplayTransitionType,
      closeTransition?:JIBO.v1.DisplayTransitions.DisplayTransitionType
    ): JIBO.v1.DisplayRequest {
        return {
            Type: CommandTypes.Display,
            View: view,
            OpenTransition: openTransition ,
            CloseTransition: closeTransition
        };
    }

    /**
     * Create DisplayToken from DisplayRequest protocol.
     * @method CommandRequester.display.Display#generateToken
     * @param {DisplayRequest} protocol - DisplayRequest protocol to generate a DisplayToken from.
     * @param {boolean} [andSend=false] - `true` if the generated DisplayToken should also be sent as a request.
     * @return {DisplayToken}
     * @intdocs
     */
    public generateToken(protocol: JIBO.v1.DisplayRequest, andSend=false): DisplayToken {
        const token = new DisplayToken(this.owner, protocol);
        if (andSend) {
            super.sendToken(token);
        }
        return token;
    }

    /**
     * Send DisplayToken request.
     * <p>NOTE: Implementation in base class {@link RequestAPI}</p>
     * @method CommandRequester.display.Display#sendToken
     * @param {DisplayToken} token - DisplayToken to send.
     * @intdocs
     */
}